import { useState, useEffect, useCallback, useMemo, useDeferredValue, useRef } from 'react';
import '../App.css';
import { useApi } from '../hooks/useApi';
import { copyText } from '../utils/clipboard';
import logger from "../utils/logger";
import Modal from './Modal';
import { filterBySearch } from '../utils/search';

const SEO_TRANSFER_KEY = 'ozon_seo_transfers';
const LARGE_KEYWORDS_THRESHOLD = 5000;
const EXTENDED_REACH_THRESHOLD = 2000;
const EMPTY_COMPUTED_DATA = {
  unique_words: [],
  plus_filtered_queries: [],
  filtered_queries: [],
  uniqualized_queries: '',
};

// Local computation helpers mirroring backend logic.

const _regexCache = new Map();
function wordMatchesPattern(word, pattern) {
  if (!pattern.includes('*')) return word === pattern;
  let regex = _regexCache.get(pattern);
  if (!regex) {
    regex = new RegExp(
      '^' + pattern.split('*').map(p => p.replace(/[.+?^${}()|[\]\\]/g, '\\$&')).join('.*') + '$'
    );
    _regexCache.set(pattern, regex);
  }
  return regex.test(word);
}

function isPlusWordProtected(word, plusWords) {
  const wl = word.toLowerCase().trim();
  return plusWords.some(pw => wordMatchesPattern(wl, pw.word.toLowerCase().trim()));
}

function normalizeQueryText(value) {
  return (value || '').trim().replace(/\s+/g, ' ').toLowerCase();
}

function _filterByPlusWords(queries, plusWordStrings, matchMode, minCount) {
  if (!plusWordStrings.length) return [];
  const plusLower = plusWordStrings.map(w => w.toLowerCase());
  return queries.filter(query => {
    const qw = query.toLowerCase().split(/\s+/);
    const hits = plusLower.reduce((n, pw) => {
      const matched = matchMode === 'soft'
        ? qw.some(w => pw.includes('*') ? wordMatchesPattern(w, pw) : w.includes(pw))
        : qw.some(w => wordMatchesPattern(w, pw));
      return n + (matched ? 1 : 0);
    }, 0);
    return hits >= minCount;
  });
}

function _filterByMinusWords(queries, minusWordStrings) {
  if (!minusWordStrings.length) return queries;
  const minusLower = minusWordStrings.map(w => w.toLowerCase());
  return queries.filter(query => {
    const qw = query.toLowerCase().split(/\s+/);
    return !minusLower.some(mw => qw.some(w => wordMatchesPattern(w, mw)));
  });
}

function _extractUniqueWords(queries) {
  const s = new Set();
  queries.forEach(q => q.split(/\s+/).forEach(w => s.add(w.toLowerCase())));
  return [...s].sort();
}

function _uniqualizeQueries(queries) {
  if (!queries.length) return '';
  const sorted = [...queries].sort((a, b) => b.length - a.length);
  const unique = [];
  // Concat all unique queries with a delimiter that can't appear in search phrases.
  // This lets us check containment with a single native string.includes() call
  // Instead of an O(n) loop, this reduces containment checks from O(n^2) to O(n).
  const DELIM = '\x01';
  let combined = '';
  for (const q of sorted) {
    if (!combined.includes(q)) {
      unique.push(q);
      combined = combined ? combined + DELIM + q : q;
    }
  }
  unique.reverse();
  return unique.join('; ');
}

function computeLocal(keywords, minusWords, plusWords, plusSettings) {
  const queries = keywords.map(kw => kw.query);
  const plusList = plusWords.map(pw => pw.word);
  const minusList = minusWords.map(mw => mw.word);
  const plusFiltered = plusList.length
    ? _filterByPlusWords(queries, plusList, plusSettings.match_mode, plusSettings.min_count)
    : [];
  const base = plusList.length ? plusFiltered : queries;
  const filtered = _filterByMinusWords(base, minusList);
  return {
    unique_words: _extractUniqueWords(filtered),
    plus_filtered_queries: plusFiltered,
    filtered_queries: filtered,
    uniqualized_queries: _uniqualizeQueries(filtered),
  };
}

function isSubseq(sub, full) {
  const sw = sub.toLowerCase().split(/\s+/);
  const fw = full.toLowerCase().split(/\s+/);
  let si = 0;
  for (let fi = 0; fi < fw.length && si < sw.length; fi++) { if (fw[fi] === sw[si]) si++; }
  return si === sw.length;
}

// Перенос в «Описания» хранится на сервере — его видят другой браузер, устройство
// и коллеги. Новый перенос заменяет прежний только по своим товарам.
const transfersPath = accountId => `/api/v1/ozon-desc/${accountId}/transfers`;

// Очередь из старой версии (localStorage) один раз переносим на сервер.
async function migrateLegacySeoTransfers(request, accountId) {
  const key = `${SEO_TRANSFER_KEY}_${accountId}`;
  let legacy = null;
  try { legacy = JSON.parse(localStorage.getItem(key)); } catch { legacy = null; }
  if (!legacy || !Object.keys(legacy).length) return;
  await request(transfersPath(accountId), { method: 'POST', body: JSON.stringify({ transfers: legacy }) });
  try { localStorage.removeItem(key); } catch { /* хранилище недоступно */ }
}

// Возвращает false при ошибке — вызывающий говорит об этом пользователю.
export async function saveSeoTransfers(request, accountId, transfers) {
  try {
    await request(transfersPath(accountId), { method: 'POST', body: JSON.stringify({ transfers }) });
    return true;
  } catch {
    return false;
  }
}
// null — очередь не загрузилась.
export async function getSeoTransfers(request, accountId) {
  try {
    await migrateLegacySeoTransfers(request, accountId);
    const data = await request(transfersPath(accountId));
    return data?.transfers || {};
  } catch {
    return null;
  }
}
// offerIds не передан — очистить всю очередь.
export async function clearSeoTransfers(request, accountId, offerIds = null) {
  try {
    await request(`${transfersPath(accountId)}/clear`, {
      method: 'POST',
      body: JSON.stringify({ offer_ids: offerIds }),
    });
    return true;
  } catch {
    return false;
  }
}
const TRANSFER_FAILED_TEXT = 'Не удалось сохранить перенос на сервере. Попробуйте ещё раз.';

function OzonSeoUniqualization({ accountId }) {
  const { authFetch, API_BASE_URL, request } = useApi();

  const [keywords, setKeywords] = useState([]);
  const [minusWords, setMinusWords] = useState([]);
  const [plusWords, setPlusWords] = useState([]);
  const [plusSettings, setPlusSettings] = useState({ match_mode: 'strict', min_count: 1 });
  const [serverComputed, setServerComputed] = useState(EMPTY_COMPUTED_DATA);
  const [busy, setBusy] = useState(false);
  const keywordUpdateTimeoutsRef = useRef(new Map());
  const keywordPendingUpdatesRef = useRef(new Map());
  const [deletingKeywordIds, setDeletingKeywordIds] = useState([]);

  // panels
  const [uniqualizedExpanded, setUniquializedExpanded] = useState(true);
  const [keywordsExpanded, setKeywordsExpanded] = useState(true);
  const [uniqueWordsExpanded, setUniqueWordsExpanded] = useState(true);
  const [plusMinusExpanded, setPlusMinusExpanded] = useState(true);

  // keywords
  const [csvText, setCsvText] = useState('');
  const [showPasteModal, setShowPasteModal] = useState(false);
  const [keywordsSearchQuery, setKeywordsSearchQuery] = useState('');
  const [keywordsPageSize, setKeywordsPageSize] = useState(50);
  const [keywordsPage, setKeywordsPage] = useState(0);

  // unique words
  const [uniqueWordsSearchQuery, setUniqueWordsSearchQuery] = useState('');
  const [uniqueWordsFilterInput, setUniqueWordsFilterInput] = useState('');
  const [uniqueWordsPageSize, setUniqueWordsPageSize] = useState(50);
  const [uniqueWordsStep, setUniqueWordsStep] = useState(0);

  // minus words
  const [minusWordInput, setMinusWordInput] = useState('');
  const [minusWordsText, setMinusWordsText] = useState('');
  const [showMinusWordsPasteModal, setShowMinusWordsPasteModal] = useState(false);
  const [minusWordsPageSize, setMinusWordsPageSize] = useState(50);
  const [minusWordsStep, setMinusWordsStep] = useState(0);
  const [minusWordsSearchQuery, setMinusWordsSearchQuery] = useState('');
  const [minusWordDeleteExact, setMinusWordDeleteExact] = useState('');
  const [pendingMinusWords, setPendingMinusWords] = useState([]);

  // plus words
  const [plusWordInput, setPlusWordInput] = useState('');
  const [plusWordsText, setPlusWordsText] = useState('');
  const [showPlusWordsPasteModal, setShowPlusWordsPasteModal] = useState(false);
  const [plusWordsPageSize, setPlusWordsPageSize] = useState(50);
  const [plusWordsStep, setPlusWordsStep] = useState(0);
  const [plusWordsSearchQuery, setPlusWordsSearchQuery] = useState('');
  const [plusWordDeleteExact, setPlusWordDeleteExact] = useState('');
  const [pendingPlusWords, setPendingPlusWords] = useState([]);

  // useDeferredValue lets React render the fast parts (word list) immediately,
  // and schedule the heavy computation (uniqualization) at lower priority.
  const deferredKeywords = useDeferredValue(keywords);
  const deferredMinusWords = useDeferredValue(minusWords);
  const deferredPlusWords = useDeferredValue(plusWords);
  const deferredPlusSettings = useDeferredValue(plusSettings);
  // Очередь «К отправке» сразу участвует в расчёте — иначе кажется, что слово не работает.
  const deferredPendingMinus = useDeferredValue(pendingMinusWords);
  const deferredPendingPlus = useDeferredValue(pendingPlusWords);
  const localComputedData = useMemo(
    () => (
      keywords.length >= LARGE_KEYWORDS_THRESHOLD
        ? EMPTY_COMPUTED_DATA
        : computeLocal(
          deferredKeywords,
          [...deferredMinusWords, ...deferredPendingMinus.map(word => ({ word }))],
          [...deferredPlusWords, ...deferredPendingPlus.map(word => ({ word }))],
          deferredPlusSettings,
        )
    ),
    [keywords.length, deferredKeywords, deferredMinusWords, deferredPlusWords, deferredPlusSettings,
      deferredPendingMinus, deferredPendingPlus]
  );
  const computedData = useMemo(
    () => (keywords.length >= LARGE_KEYWORDS_THRESHOLD ? (serverComputed || EMPTY_COMPUTED_DATA) : localComputedData),
    [keywords.length, localComputedData, serverComputed]
  );


  // products / transfer
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [transferMessage, setTransferMessage] = useState(null);

  // add uniqualized keywords: to product's own OZON SEO field, or to АвтоSEO dictionary
  const [addToProductSeoBusy, setAddToProductSeoBusy] = useState(false);
  const [addToProductSeoMessage, setAddToProductSeoMessage] = useState(null);
  const [addToAutoSeoBusy, setAddToAutoSeoBusy] = useState(false);
  const [addToAutoSeoMessage, setAddToAutoSeoMessage] = useState(null);
  const [autoSeoDictionaries, setAutoSeoDictionaries] = useState([]);
  const [autoSeoDictionaryId, setAutoSeoDictionaryId] = useState(null);
  const [showAutoSeoDictModal, setShowAutoSeoDictModal] = useState(false);
  const [autoSeoNewDictName, setAutoSeoNewDictName] = useState('');
  const [autoSeoCreatingDict, setAutoSeoCreatingDict] = useState(false);

  // self-contained product picker for "Добавить в SEO товару" — opened by the
  // button itself, independent of the page-level "Выбор товаров" section
  const [showProductPickerModal, setShowProductPickerModal] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerResults, setPickerResults] = useState([]);
  const [pickerSelected, setPickerSelected] = useState([]);

  // context menu on uniqualized box
  const [ctxMenu, setCtxMenu] = useState(null); // { x, y, text }

  // Load data.

  const loadData = useCallback(async () => {
    if (!accountId) return;
    setBusy(true);
    try {
      const data = await authFetch(`${API_BASE_URL}/api/v1/ozon-seo/${accountId}/computed`);
      setKeywords(data.keywords);
      setMinusWords(data.minus_words);
      setPlusWords(data.plus_words || []);
      setPlusSettings(data.plus_settings || { match_mode: 'strict', min_count: 1 });
      setServerComputed(data.computed || EMPTY_COMPUTED_DATA);
    } catch (error) {
      logger.error('Failed to load SEO data:', error);
      setTransferMessage({ text: 'Не удалось загрузить данные уникализации: ' + (error.message || error) });
    } finally {
      setBusy(false);
    }
  }, [accountId, authFetch, API_BASE_URL]);

  const loadComputedData = useCallback(async ({ showBusy = true } = {}) => {
    if (!accountId) return;
    if (showBusy) {
      setBusy(true);
    }
    try {
      const data = await authFetch(`${API_BASE_URL}/api/v1/ozon-seo/${accountId}/computed?include_keywords=false`);
      setMinusWords(data.minus_words);
      setPlusWords(data.plus_words || []);
      setPlusSettings(data.plus_settings || { match_mode: 'strict', min_count: 1 });
      setServerComputed(data.computed || EMPTY_COMPUTED_DATA);
    } catch (error) {
      logger.error('Failed to load computed SEO data:', error);
    } finally {
      if (showBusy) {
        setBusy(false);
      }
    }
  }, [accountId, authFetch, API_BASE_URL]);

  const loadAutoSeoDictionaries = useCallback(async () => {
    if (!accountId) return;
    try {
      const data = await authFetch(`${API_BASE_URL}/api/v1/ozon-auto-seo/${accountId}/dictionaries`);
      // Словари, розданные на проект другими кабинетами, только для чтения — дописывать в них нельзя.
      const list = (data || []).filter(d => d.is_own !== false);
      setAutoSeoDictionaries(list);
      setAutoSeoDictionaryId(prev => (prev && list.some(d => d.id === prev)) ? prev : (list[0]?.id || null));
    } catch (error) {
      logger.error('Failed to load АвтоSEO dictionaries:', error);
    }
  }, [accountId, authFetch, API_BASE_URL]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { loadAutoSeoDictionaries(); }, [loadAutoSeoDictionaries]);
  useEffect(() => () => {
    keywordUpdateTimeoutsRef.current.forEach(timeoutId => clearTimeout(timeoutId));
    keywordUpdateTimeoutsRef.current.clear();
    keywordPendingUpdatesRef.current.clear();
  }, []);

  useEffect(() => {
    if (!pendingMinusWords.length && !pendingPlusWords.length) return undefined;
    const warn = event => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [pendingMinusWords.length, pendingPlusWords.length]);

  // Keywords.

  const addKeyword = async (query, popularity, rowOrder) => {
    try {
      await authFetch(`${API_BASE_URL}/api/v1/ozon-seo/${accountId}/keywords`, {
        method: 'POST',
        body: JSON.stringify({ query, popularity, row_order: rowOrder })
      });
      await loadData();
    } catch (error) { logger.error('Failed to add keyword:', error); }
  };

  const updateKeyword = (keywordId, updates) => {
    setKeywords(prev => prev.map(keyword => (
      keyword.id === keywordId ? { ...keyword, ...updates } : keyword
    )));

    const pendingUpdates = {
      ...(keywordPendingUpdatesRef.current.get(keywordId) || {}),
      ...updates
    };
    keywordPendingUpdatesRef.current.set(keywordId, pendingUpdates);

    const existingTimeoutId = keywordUpdateTimeoutsRef.current.get(keywordId);
    if (existingTimeoutId) {
      clearTimeout(existingTimeoutId);
    }

    const timeoutId = setTimeout(async () => {
      const payload = keywordPendingUpdatesRef.current.get(keywordId);
      keywordUpdateTimeoutsRef.current.delete(keywordId);
      keywordPendingUpdatesRef.current.delete(keywordId);
      if (!payload) {
        return;
      }
      try {
        await authFetch(`${API_BASE_URL}/api/v1/ozon-seo/${accountId}/keywords/${keywordId}`, {
          method: 'PATCH', body: JSON.stringify(payload)
        });
        await loadComputedData({ showBusy: false });
      } catch (error) {
        logger.error('Failed to update keyword:', error);
        await loadData();
      }
    }, 600);

    keywordUpdateTimeoutsRef.current.set(keywordId, timeoutId);
  };

  const deleteKeyword = async keywordId => {
    if (deletingKeywordIds.includes(keywordId)) {
      return;
    }
    const existingTimeoutId = keywordUpdateTimeoutsRef.current.get(keywordId);
    if (existingTimeoutId) {
      clearTimeout(existingTimeoutId);
      keywordUpdateTimeoutsRef.current.delete(keywordId);
      keywordPendingUpdatesRef.current.delete(keywordId);
    }
    const previousKeywords = keywords;
    setDeletingKeywordIds(prev => [...prev, keywordId]);
    setKeywords(prev => prev.filter(keyword => keyword.id !== keywordId));
    setBusy(true);
    try {
      await authFetch(`${API_BASE_URL}/api/v1/ozon-seo/${accountId}/keywords/${keywordId}`, { method: 'DELETE' });
      await loadComputedData({ showBusy: false });
    } catch (error) {
      logger.error('Failed to delete keyword:', error);
      setKeywords(previousKeywords);
    } finally {
      setDeletingKeywordIds(prev => prev.filter(id => id !== keywordId));
      setBusy(false);
    }
  };

  const handleCSVImport = async () => {
    if (!csvText.trim()) return;
    const lines = csvText.trim().split('\n');
    const keywordsData = [];
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      if (i === 0 && (line.toLowerCase().includes('запрос') || line.toLowerCase().includes('query'))) continue;
      const parts = line.split(/[,\t]/);
      if (parts.length >= 1) {
        const query = parts[0].trim();
        const popularity = parts[1] ? parseInt(parts[1].trim()) || null : null;
        keywordsData.push({ query, popularity, row_order: keywordsData.length });
      }
    }
    if (keywordsData.length > 0) {
      setBusy(true);
      try {
        await authFetch(`${API_BASE_URL}/api/v1/ozon-seo/${accountId}/keywords/bulk-import`, {
          method: 'POST', body: JSON.stringify({ keywords: keywordsData })
        });
        await loadData(); setCsvText(''); setShowPasteModal(false);
      } catch (error) {
        logger.error('Failed to import keywords:', error);
        alert('Ошибка импорта: ' + error.message);
      } finally { setBusy(false); }
    }
  };

  const downloadCSV = () => {
    const csvContent = 'Запрос;Популярность запроса\n' + keywords.map(k => `"${k.query}";${k.popularity || ''}`).join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'ozon_seo_keywords.csv';
    link.click();
  };

  // Minus words.

  const addMinusWord = word => {
    if (!word?.trim()) return;
    const w = word.trim();
    const protectedPlusWords = [...plusWords, ...pendingPlusWords.map(word => ({ word }))];
    const conflict = protectedPlusWords.find(pw => wordMatchesPattern(w.toLowerCase(), pw.word.toLowerCase().trim()));
    if (conflict) {
      alert(`Нельзя минусовать «${w}» — оно защищено плюс-словом «${conflict.word}»`);
      return;
    }
    const normalizedWord = normalizeQueryText(w);
    if (!normalizedWord) {
      return;
    }
    const existsSaved = minusWords.some(mw => normalizeQueryText(mw.word) === normalizedWord);
    const existsPending = pendingMinusWords.some(word => normalizeQueryText(word) === normalizedWord);
    if (existsSaved || existsPending) {
      return;
    }
    setPendingMinusWords(prev => [...prev, w]);
  };

  const deleteMinusWord = async minusWordId => {
    if (keywords.length >= LARGE_KEYWORDS_THRESHOLD) {
      try {
        await authFetch(`${API_BASE_URL}/api/v1/ozon-seo/${accountId}/minus-words/${minusWordId}`, { method: 'DELETE' });
        await loadComputedData();
      } catch (error) {
        logger.error('Failed to delete minus word:', error);
      }
      return;
    }
    const removed = minusWords.find(m => m.id === minusWordId);
    const newMinus = minusWords.filter(m => m.id !== minusWordId);
    setMinusWords(newMinus);
    try {
      await authFetch(`${API_BASE_URL}/api/v1/ozon-seo/${accountId}/minus-words/${minusWordId}`, { method: 'DELETE' });
    } catch (error) {
      logger.error('Failed to delete minus word:', error);
      if (removed) setMinusWords(prev => [...prev, removed].sort((a, b) => (a.row_order ?? 0) - (b.row_order ?? 0)));
    }
  };

  const deleteMinusWordByExact = async () => {
    const target = minusWordDeleteExact.trim();
    if (!target) return;
    const matches = minusWords.filter(mw => mw.word.trim().toLowerCase() === target.toLowerCase());
    if (matches.length === 0) { alert('Минус-слово не найдено.'); return; }
    if (matches.length > 1 && !window.confirm(`Найдено ${matches.length} совпадений. Удалить все?`)) return;
    if (keywords.length >= LARGE_KEYWORDS_THRESHOLD) {
      setBusy(true);
      try {
        await Promise.all(matches.map(mw =>
          authFetch(`${API_BASE_URL}/api/v1/ozon-seo/${accountId}/minus-words/${mw.id}`, { method: 'DELETE' })
        ));
        await loadComputedData({ showBusy: false });
        setMinusWordDeleteExact('');
      } catch (error) {
        alert('Ошибка удаления: ' + error.message);
      } finally { setBusy(false); }
      return;
    }
    const ids = new Set(matches.map(m => m.id));
    const newMinus = minusWords.filter(m => !ids.has(m.id));
    setMinusWords(newMinus);
    setBusy(true);
    try {
      await Promise.all(matches.map(mw =>
        authFetch(`${API_BASE_URL}/api/v1/ozon-seo/${accountId}/minus-words/${mw.id}`, { method: 'DELETE' })
      ));
      setMinusWordDeleteExact('');
    } catch (error) {
      alert('Ошибка удаления: ' + error.message);
      setMinusWords(minusWords);
    } finally { setBusy(false); }
  };

  const handleMinusWordsImport = async () => {
    if (!minusWordsText.trim()) return;
    let words = minusWordsText.trim().split('\n')
      .map(l => l.trim()).filter(w => w && w.toLowerCase() !== 'минус-слово' && w.toLowerCase() !== 'minus word');
    const blocked = words.filter(w => isPlusWordProtected(w, plusWords));
    words = words.filter(w => !isPlusWordProtected(w, plusWords));
    if (words.length === 0) { alert('Все слова защищены плюс-словами'); return; }
    const blockedMsg = blocked.length > 0 ? ` (${blocked.length} пропущено — плюс-слова)` : '';
    setBusy(true);
    try {
      await authFetch(`${API_BASE_URL}/api/v1/ozon-seo/${accountId}/minus-words/bulk-add`, {
        method: 'POST', body: JSON.stringify(words)
      });
      await loadComputedData({ showBusy: false }); setMinusWordsText(''); setShowMinusWordsPasteModal(false);
      alert(`Добавлено ${words.length} минус-слов(а)${blockedMsg}`);
    } catch (error) { alert('Ошибка импорта: ' + error.message); } finally { setBusy(false); }
  };

  const applyPendingMinusWords = async () => {
    if (pendingMinusWords.length === 0) return;
    setBusy(true);
    try {
      await authFetch(`${API_BASE_URL}/api/v1/ozon-seo/${accountId}/minus-words/bulk-add`, {
        method: 'POST', body: JSON.stringify(pendingMinusWords)
      });
      setPendingMinusWords([]);
      await loadComputedData({ showBusy: false });
    } catch (error) {
      alert('Ошибка применения минус-слов: ' + error.message);
    } finally { setBusy(false); }
  };

  // Plus words.

  const addPlusWord = word => {
    if (!word?.trim()) return;
    const w = word.trim();
    const normalizedWord = normalizeQueryText(w);
    if (!normalizedWord) {
      return;
    }
    const existsSaved = plusWords.some(pw => normalizeQueryText(pw.word) === normalizedWord);
    const existsPending = pendingPlusWords.some(word => normalizeQueryText(word) === normalizedWord);
    if (existsSaved || existsPending) {
      return;
    }
    setPendingPlusWords(prev => [...prev, w]);
  };

  const deletePlusWord = async plusWordId => {
    if (keywords.length >= LARGE_KEYWORDS_THRESHOLD) {
      try {
        await authFetch(`${API_BASE_URL}/api/v1/ozon-seo/${accountId}/plus-words/${plusWordId}`, { method: 'DELETE' });
        await loadComputedData();
      } catch (error) {
        logger.error('Failed to delete plus word:', error);
      }
      return;
    }
    const removed = plusWords.find(pw => pw.id === plusWordId);
    const newPlus = plusWords.filter(pw => pw.id !== plusWordId);
    setPlusWords(newPlus);
    try {
      await authFetch(`${API_BASE_URL}/api/v1/ozon-seo/${accountId}/plus-words/${plusWordId}`, { method: 'DELETE' });
    } catch (error) {
      logger.error('Failed to delete plus word:', error);
      if (removed) setPlusWords(prev => [...prev, removed].sort((a, b) => (a.row_order ?? 0) - (b.row_order ?? 0)));
    }
  };

  const deletePlusWordByExact = async () => {
    const target = plusWordDeleteExact.trim();
    if (!target) return;
    const matches = plusWords.filter(pw => pw.word.trim().toLowerCase() === target.toLowerCase());
    if (matches.length === 0) { alert('Плюс-слово не найдено.'); return; }
    if (matches.length > 1 && !window.confirm(`Найдено ${matches.length} совпадений. Удалить все?`)) return;
    if (keywords.length >= LARGE_KEYWORDS_THRESHOLD) {
      setBusy(true);
      try {
        await Promise.all(matches.map(pw =>
          authFetch(`${API_BASE_URL}/api/v1/ozon-seo/${accountId}/plus-words/${pw.id}`, { method: 'DELETE' })
        ));
        await loadComputedData({ showBusy: false });
        setPlusWordDeleteExact('');
      } catch (error) {
        alert('Ошибка удаления: ' + error.message);
      } finally { setBusy(false); }
      return;
    }
    const ids = new Set(matches.map(p => p.id));
    const newPlus = plusWords.filter(p => !ids.has(p.id));
    setPlusWords(newPlus);
    setBusy(true);
    try {
      await Promise.all(matches.map(pw =>
        authFetch(`${API_BASE_URL}/api/v1/ozon-seo/${accountId}/plus-words/${pw.id}`, { method: 'DELETE' })
      ));
      setPlusWordDeleteExact('');
    } catch (error) {
      alert('Ошибка удаления: ' + error.message);
      setPlusWords(plusWords);
    } finally { setBusy(false); }
  };

  const handlePlusWordsImport = async () => {
    if (!plusWordsText.trim()) return;
    const words = plusWordsText.trim().split('\n')
      .map(l => l.trim()).filter(w => w && w.toLowerCase() !== 'плюс-слово');
    if (words.length > 0) {
      setBusy(true);
      const firstNewIndex = plusWords.length;
      try {
        await authFetch(`${API_BASE_URL}/api/v1/ozon-seo/${accountId}/plus-words/bulk-add`, {
          method: 'POST', body: JSON.stringify(words)
        });
        await loadComputedData({ showBusy: false }); setPlusWordsText(''); setShowPlusWordsPasteModal(false);
        // Jump to the page where first new word landed
        if (plusWordsPageSize !== Infinity) {
          setPlusWordsStep(Math.floor(firstNewIndex / plusWordsPageSize));
        }
        alert(`Добавлено ${words.length} плюс-слов(а)`);
      } catch (error) { alert('Ошибка импорта: ' + error.message); } finally { setBusy(false); }
    }
  };

  const applyPendingPlusWords = async () => {
    if (pendingPlusWords.length === 0) return;
    const firstNewIndex = plusWords.length;
    setBusy(true);
    try {
      await authFetch(`${API_BASE_URL}/api/v1/ozon-seo/${accountId}/plus-words/bulk-add`, {
        method: 'POST', body: JSON.stringify(pendingPlusWords)
      });
      setPendingPlusWords([]);
      await loadComputedData({ showBusy: false });
      if (plusWordsPageSize !== Infinity) {
        setPlusWordsStep(Math.floor(firstNewIndex / plusWordsPageSize));
      }
    } catch (error) {
      alert('Ошибка применения плюс-слов: ' + error.message);
    } finally { setBusy(false); }
  };

  const updatePlusSettings = async (patch) => {
    setPlusSettings(prev => ({ ...prev, ...patch }));
    try {
      await authFetch(`${API_BASE_URL}/api/v1/ozon-seo/${accountId}/plus-settings`, {
        method: 'PATCH', body: JSON.stringify(patch)
      });
      if (keywords.length >= LARGE_KEYWORDS_THRESHOLD) {
        await loadComputedData();
      }
    } catch (error) { logger.error('Failed to update plus settings:', error); }
  };

  // Bulk minus helpers.

  const bulkMinus = (words, label) => {
    const protectedPlusWords = [...plusWords, ...pendingPlusWords.map(word => ({ word }))];
    const allowed = words.filter(w => !isPlusWordProtected(w, protectedPlusWords));
    const skipped = words.length - allowed.length;
    if (allowed.length === 0) { alert(`Нет слов ${label}${skipped > 0 ? ' (все защищены плюс-словами)' : ''}`); return; }
    const existingMinus = new Set(minusWords.map(mw => normalizeQueryText(mw.word)));
    const queuedMinus = new Set(pendingMinusWords.map(word => normalizeQueryText(word)));
    const queued = allowed.filter(word => {
      const normalizedWord = normalizeQueryText(word);
      return normalizedWord && !existingMinus.has(normalizedWord) && !queuedMinus.has(normalizedWord);
    });
    if (queued.length === 0) { alert('Новых слов для очереди нет'); return; }
    setPendingMinusWords(prev => [...prev, ...queued]);
    const skippedMsg = skipped > 0 ? ` (${skipped} пропущено — плюс-слова)` : '';
    alert(`Добавлено в очередь: ${queued.length} слов(а)${skippedMsg}`);
  };

  const bulkMinusFiltered = () => {
    if (!uniqueWordsFilterInput.trim()) { alert('Введите фильтр'); return; }
    const protectedPlusWords = [...plusWords, ...pendingPlusWords.map(word => ({ word }))];
    const allowed = filterInputWords.filter(w => !isPlusWordProtected(w, protectedPlusWords));
    const skipped = filterInputWords.length - allowed.length;
    if (allowed.length === 0) { alert('Нет слов для добавления'); return; }
    const existingMinus = new Set(minusWords.map(mw => normalizeQueryText(mw.word)));
    const queuedMinus = new Set(pendingMinusWords.map(word => normalizeQueryText(word)));
    const queued = allowed.filter(word => {
      const normalizedWord = normalizeQueryText(word);
      return normalizedWord && !existingMinus.has(normalizedWord) && !queuedMinus.has(normalizedWord);
    });
    if (queued.length === 0) { alert('Новых слов для очереди нет'); return; }
    setPendingMinusWords(prev => [...prev, ...queued]);
    setUniqueWordsFilterInput('');
    const skippedMsg = skipped > 0 ? ` (${skipped} пропущено — плюс-слова)` : '';
    alert(`Добавлено в очередь: ${queued.length} слов(а)${skippedMsg}`);
  };

  // Copy actions.

  const copyUniqualized = () => { copyText(computedData.uniqualized_queries); alert('Скопировано!'); };
  const copyUniquializedAsColumn = () => {
    copyText(computedData.uniqualized_queries.split(';').filter(s => s.trim()).join('\n'));
    alert('Скопировано в столбец!');
  };
  const copyUniquializedWithPopularity = () => {
    const popularityByQuery = new Map();
    keywords.forEach(keyword => {
      const normalizedQuery = normalizeQueryText(keyword.query);
      if (!normalizedQuery) return;
      const nextPopularity = keyword.popularity ?? '';
      if (!popularityByQuery.has(normalizedQuery)) {
        popularityByQuery.set(normalizedQuery, nextPopularity);
        return;
      }
      const currentPopularity = popularityByQuery.get(normalizedQuery);
      if (typeof nextPopularity === 'number' && (currentPopularity === '' || nextPopularity > currentPopularity)) {
        popularityByQuery.set(normalizedQuery, nextPopularity);
      }
    });
    const rows = computedData.uniqualized_queries
      .split(';')
      .map(s => s.trim())
      .filter(Boolean)
      .map(query => `${query}\t${popularityByQuery.get(normalizeQueryText(query)) ?? ''}`);
    copyText(rows.join('\n'));
    alert('Скопировано в столбец с частотностью!');
  };

  // Products.

  useEffect(() => {
    if (!productSearch) { setProductResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        const data = await authFetch(`${API_BASE_URL}/api/v1/ozon/${accountId}/attributes?search=${encodeURIComponent(productSearch)}&limit=10`);
        setProductResults(data.rows || []);
      } catch { setProductResults([]); }
    }, 300);
    return () => clearTimeout(timer);
  }, [productSearch, accountId]);

  const addProduct = (product, keepOpen = false) => {
    const offerId = product[2];
    if (selectedProducts.find(p => p.offer_id === offerId)) {
      setSelectedProducts(prev => prev.filter(p => p.offer_id !== offerId));
      if (!keepOpen) setProductResults([]);
      return;
    }
    setSelectedProducts(prev => [...prev, { offer_id: offerId, name: product[1], sku: product[3], image: product[11], description: '' }]);
    if (!keepOpen) setProductResults([]);
  };

  // Standalone search for the "Добавить в SEO товару" picker modal — kept
  // separate from the page-level product selector above so the modal is
  // fully self-contained.
  useEffect(() => {
    if (!showProductPickerModal || !pickerSearch) { setPickerResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        const data = await authFetch(`${API_BASE_URL}/api/v1/ozon/${accountId}/attributes?search=${encodeURIComponent(pickerSearch)}&limit=10`);
        setPickerResults(data.rows || []);
      } catch { setPickerResults([]); }
    }, 300);
    return () => clearTimeout(timer);
  }, [showProductPickerModal, pickerSearch, accountId]);

  const togglePickerProduct = product => {
    const offerId = product[2];
    setPickerSelected(prev => (
      prev.find(p => p.offer_id === offerId)
        ? prev.filter(p => p.offer_id !== offerId)
        : [...prev, { offer_id: offerId, name: product[1], sku: product[3], image: product[11] }]
    ));
  };

  const transferToSeo = async () => {
    if (selectedProducts.length === 0) { alert('Выберите хотя бы один товар'); return; }
    if (!computedData.uniqualized_queries?.trim()) { alert('Нет уникализированных запросов'); return; }
    const transfers = {};
    for (const product of selectedProducts) {
      transfers[product.offer_id] = { originalDesc: product.description || '', seoText: computedData.uniqualized_queries, productName: product.name };
    }
    if (!(await saveSeoTransfers(request, accountId, transfers))) {
      setTransferMessage({ text: TRANSFER_FAILED_TEXT });
      return;
    }
    setTransferMessage({ text: `Перенесено в ${selectedProducts.length} товар(ов). Перейдите в раздел SEO → Описания.` });
    setSelectedProducts([]);
    setTimeout(() => setTransferMessage(null), 10000);
  };

  // Push uniqualized keywords out: into a product's own OZON SEO field, or into
  // the АвтоSEO semantics dictionary.

  // Clicking "+ Добавить в SEO товару" opens a self-contained picker modal —
  // the product(s) are chosen right there, not from a pre-existing page-level
  // selection the user has to find first.
  const openProductPickerModal = () => {
    if (!computedData.uniqualized_queries?.trim()) { alert('Нет уникализированных запросов'); return; }
    setPickerSearch('');
    setPickerResults([]);
    // Pre-seed with whatever is already checked in the page-level selector
    // (if the user happened to use it), but the modal works standalone too.
    setPickerSelected(selectedProducts.map(p => ({ offer_id: p.offer_id, name: p.name, sku: p.sku, image: p.image })));
    setShowProductPickerModal(true);
  };

  const confirmAddUniqualizedToProductSeo = async () => {
    if (!computedData.uniqualized_queries?.trim()) { alert('Нет уникализированных запросов'); return; }
    if (pickerSelected.length === 0) { alert('Выберите хотя бы один товар'); return; }
    const kws = computedData.uniqualized_queries.split(';').map(s => s.trim()).filter(Boolean);
    setAddToProductSeoBusy(true);
    setAddToProductSeoMessage(null);
    try {
      const data = await authFetch(`${API_BASE_URL}/api/v1/ozon-desc/${accountId}/keywords`, {
        method: 'POST',
        body: JSON.stringify({
          items: pickerSelected.map(p => ({ offer_id: p.offer_id, keywords: kws }))
        })
      });
      const skippedNote = data.items_skipped ? `, пропущено: ${data.items_skipped}` : '';
      setAddToProductSeoMessage({ type: 'ok', text: `Отправлено в SEO OZON: ${data.items_sent} товар(ов)${skippedNote}` });
      setShowProductPickerModal(false);
      setPickerSelected([]);
    } catch (error) {
      setAddToProductSeoMessage({ type: 'error', text: 'Ошибка: ' + error.message });
    } finally {
      setAddToProductSeoBusy(false);
      setTimeout(() => setAddToProductSeoMessage(null), 6000);
    }
  };

  const openAddToAutoSeoDictModal = () => {
    if (!computedData.uniqualized_queries?.trim()) { alert('Нет уникализированных запросов'); return; }
    setAutoSeoNewDictName('');
    setShowAutoSeoDictModal(true);
  };

  const createAutoSeoDictionary = async () => {
    const name = autoSeoNewDictName.trim();
    if (!name) return;
    setAutoSeoCreatingDict(true);
    try {
      const dictionary = await authFetch(`${API_BASE_URL}/api/v1/ozon-auto-seo/${accountId}/dictionaries`, {
        method: 'POST', body: JSON.stringify({ name })
      });
      await loadAutoSeoDictionaries();
      setAutoSeoDictionaryId(dictionary.id);
      setAutoSeoNewDictName('');
    } catch (error) {
      alert('Ошибка создания словаря: ' + error.message);
    } finally {
      setAutoSeoCreatingDict(false);
    }
  };

  const confirmAddUniqualizedToAutoSeoDictionary = async () => {
    if (!computedData.uniqualized_queries?.trim()) { alert('Нет уникализированных запросов'); return; }
    const kws = computedData.uniqualized_queries.split(';').map(s => s.trim()).filter(Boolean);
    // Same lookup as "Копировать в столбец с частотностью" — pulls each query's
    // popularity from the loaded keywords instead of sending 0 for everything.
    const popularityByQuery = new Map();
    keywords.forEach(keyword => {
      const normalizedQuery = normalizeQueryText(keyword.query);
      if (!normalizedQuery) return;
      const nextPopularity = keyword.popularity ?? '';
      if (!popularityByQuery.has(normalizedQuery)) {
        popularityByQuery.set(normalizedQuery, nextPopularity);
        return;
      }
      const currentPopularity = popularityByQuery.get(normalizedQuery);
      if (typeof nextPopularity === 'number' && (currentPopularity === '' || nextPopularity > currentPopularity)) {
        popularityByQuery.set(normalizedQuery, nextPopularity);
      }
    });
    setAddToAutoSeoBusy(true);
    setAddToAutoSeoMessage(null);
    try {
      const data = await authFetch(`${API_BASE_URL}/api/v1/ozon-auto-seo/${accountId}/semantics/bulk-add`, {
        method: 'POST',
        body: JSON.stringify({
          rows: kws.map(q => ({ query: q, frequency: popularityByQuery.get(normalizeQueryText(q)) || 0 })),
          dictionary_id: autoSeoDictionaryId || undefined,
        })
      });
      setAddToAutoSeoMessage({ type: 'ok', text: `Добавлено в словарь «${data.dictionary_name}»: ${data.count} запрос(ов)` });
      setAutoSeoDictionaryId(data.dictionary_id);
      setShowAutoSeoDictModal(false);
    } catch (error) {
      setAddToAutoSeoMessage({ type: 'error', text: 'Ошибка: ' + error.message });
    } finally {
      setAddToAutoSeoBusy(false);
      setTimeout(() => setAddToAutoSeoMessage(null), 6000);
    }
  };

  // close context menu on any click
  useEffect(() => {
    if (!ctxMenu) return;
    const close = () => setCtxMenu(null);
    window.addEventListener('click', close);
    window.addEventListener('keydown', close);
    return () => { window.removeEventListener('click', close); window.removeEventListener('keydown', close); };
  }, [ctxMenu]);

  // close product dropdown on outside click
  useEffect(() => {
    if (!productResults.length) return;
    const close = () => setProductResults([]);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [productResults.length]);

  // Derived data.

  const uniqualizedCount = computedData.uniqualized_queries.split(';').filter(s => s.trim()).length;
  const uniqualizedChars = computedData.uniqualized_queries.length;
  const isOverLimit = uniqualizedChars > 10000;
  const hasPlusWords = plusWords.length > 0;

  const uniqualizedList = useMemo(
    () => computedData.uniqualized_queries.split(';').map(s => s.trim()).filter(Boolean),
    [computedData.uniqualized_queries]
  );

  const { directReach, extendedReach } = useMemo(() => {
    const uniqualizedSet = new Set(uniqualizedList.map(s => s.toLowerCase()));
    const directReachValue = keywords
      .filter(kw => uniqualizedSet.has((kw.query || '').trim().toLowerCase()))
      .reduce((sum, kw) => sum + (kw.popularity || 0), 0);
    if (keywords.length > EXTENDED_REACH_THRESHOLD || uniqualizedList.length > EXTENDED_REACH_THRESHOLD) {
      return { directReach: directReachValue, extendedReach: null };
    }
    const coveredIds = new Set();
    uniqualizedList.forEach(uq => {
      keywords.forEach(kw => {
        if (!coveredIds.has(kw.id) && isSubseq((kw.query || '').trim(), uq)) {
          coveredIds.add(kw.id);
        }
      });
    });
    const extendedReachValue = keywords
      .filter(kw => coveredIds.has(kw.id))
      .reduce((sum, kw) => sum + (kw.popularity || 0), 0);
    return { directReach: directReachValue, extendedReach: extendedReachValue };
  }, [uniqualizedList, keywords]);
  const filteredKeywords = filterBySearch(keywords, keywordsSearchQuery);
  const keywordsTotalPages = Math.ceil(filteredKeywords.length / keywordsPageSize);
  const keywordsStart = keywordsPage * keywordsPageSize;
  const keywordsEnd = Math.min(keywordsStart + keywordsPageSize, filteredKeywords.length);
  const keywordsPageData = keywordsPageSize === Infinity ? filteredKeywords : filteredKeywords.slice(keywordsStart, keywordsEnd);

  const filteredUniqueWords = filterBySearch(computedData.unique_words, uniqueWordsSearchQuery);
  const filterInputWords = filterBySearch(filteredUniqueWords, uniqueWordsFilterInput);
  const uniqueWordsTotal = filterInputWords.length;
  const uniqueWordsTotalPages = Math.ceil(uniqueWordsTotal / uniqueWordsPageSize);
  const uniqueWordsPageStart = uniqueWordsStep * uniqueWordsPageSize;
  const uniqueWordsPageEnd = Math.min(uniqueWordsPageStart + uniqueWordsPageSize, uniqueWordsTotal);
  const uniqueWordsPage = uniqueWordsPageSize === Infinity ? filterInputWords : filterInputWords.slice(uniqueWordsPageStart, uniqueWordsPageEnd);

  const filteredMinusWords = filterBySearch(minusWords, minusWordsSearchQuery);
  const minusWordsTotal = filteredMinusWords.length;
  const minusWordsTotalPages = Math.ceil(minusWordsTotal / minusWordsPageSize);
  const minusWordsPageStart = minusWordsStep * minusWordsPageSize;
  const minusWordsPageEnd = Math.min(minusWordsPageStart + minusWordsPageSize, minusWordsTotal);
  const minusWordsPage = minusWordsPageSize === Infinity ? filteredMinusWords : filteredMinusWords.slice(minusWordsPageStart, minusWordsPageEnd);

  const filteredPlusWords = filterBySearch(plusWords, plusWordsSearchQuery);
  const plusWordsTotal = filteredPlusWords.length;
  const plusWordsTotalPages = Math.ceil(plusWordsTotal / plusWordsPageSize);
  const plusWordsPageStart = plusWordsStep * plusWordsPageSize;
  const plusWordsPageEnd = Math.min(plusWordsPageStart + plusWordsPageSize, plusWordsTotal);
  const plusWordsPageData = plusWordsPageSize === Infinity ? filteredPlusWords : filteredPlusWords.slice(plusWordsPageStart, plusWordsPageEnd);

  // Render.

  return (
    <div className="ozon-seo-panel">

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <button type="button" className="btn-secondary btn-sm" onClick={() => loadData()} disabled={busy}>↺ Обновить</button>
      </div>

      {transferMessage && (
        <div className="seo-transfer-message">
          <span>{transferMessage.text}</span>
          <button onClick={() => setTransferMessage(null)}>×</button>
        </div>
      )}

      {/* 1. Запросы */}
      <div className="seo-keywords-zone">
        <div className="seo-keywords-header" onClick={() => setKeywordsExpanded(!keywordsExpanded)}>
          <h4>{keywordsExpanded ? '▼' : '▶'} Запросы ({keywords.length} шт.)</h4>
          {keywordsExpanded && keywords.length > 0 && (
            <div className="seo-keywords-controls" onClick={e => e.stopPropagation()}>
              <label>Строк на странице:
                <select value={keywordsPageSize === Infinity ? 'all' : keywordsPageSize}
                  onChange={e => { setKeywordsPageSize(e.target.value === 'all' ? Infinity : parseInt(e.target.value)); setKeywordsPage(0); }}
                  className="page-size-select">
                  <option value="50">50</option><option value="250">250</option>
                  <option value="500">500</option><option value="1000">1000</option><option value="all">Все</option>
                </select>
              </label>
              {keywordsPageSize !== Infinity && keywordsTotalPages > 1 && (
                <div className="pagination-controls">
                  <button className="btn btn-sm" onClick={() => setKeywordsPage(Math.max(0, keywordsPage - 1))} disabled={keywordsPage === 0}>←</button>
                  <span>{keywordsStart + 1}-{keywordsEnd} из {filteredKeywords.length}</span>
                  <button className="btn btn-sm" onClick={() => setKeywordsPage(Math.min(keywordsTotalPages - 1, keywordsPage + 1))} disabled={keywordsPage >= keywordsTotalPages - 1}>→</button>
                </div>
              )}
            </div>
          )}
        </div>
        {keywordsExpanded && (
          <>
            <div className="seo-table-actions" style={{ padding: '16px', borderTop: '1px solid var(--border)' }}>
              <button className="btn btn-secondary" onClick={() => setShowPasteModal(true)}>Вставить данные</button>
              <button className="btn btn-secondary" onClick={downloadCSV} disabled={busy || keywords.length === 0}>Скачать CSV</button>
              <button className="btn btn-secondary" onClick={() => {
                if (window.confirm('Очистить все данные?'))
                  authFetch(`${API_BASE_URL}/api/v1/ozon-seo/${accountId}/keywords`, { method: 'DELETE' })
                    .then(loadData)
                    .catch(error => alert('Не удалось очистить запросы: ' + (error.message || error)));
              }} disabled={busy || keywords.length === 0}>Очистить всё</button>
              <input type="text" placeholder="Поиск по запросам..." value={keywordsSearchQuery}
                onChange={e => { setKeywordsSearchQuery(e.target.value); setKeywordsPage(0); }}
                className="table-search" style={{ marginLeft: 'auto' }} />
            </div>
            <div className="seo-table-container">
              <table className="seo-table">
                <thead>
                  <tr>
                    <th style={{ width: '3%' }}>−</th>
                    <th style={{ width: '5%' }}>№</th>
                    <th style={{ width: '50%' }}>Запрос</th>
                    <th style={{ width: '42%' }}>Популярность</th>
                  </tr>
                </thead>
                <tbody>
                  {keywordsPageData.map((keyword, idx) => (
                    <tr key={keyword.id}>
                      <td><button className="btn-delete-row" onClick={() => deleteKeyword(keyword.id)} disabled={deletingKeywordIds.includes(keyword.id)}>−</button></td>
                      <td>{keywordsStart + idx + 1}</td>
                      <td><input type="text" value={keyword.query} onChange={e => updateKeyword(keyword.id, { query: e.target.value })} className="cell-input" /></td>
                      <td><input type="number" value={keyword.popularity || ''} onChange={e => updateKeyword(keyword.id, { popularity: parseInt(e.target.value) || null })} className="cell-input" /></td>
                    </tr>
                  ))}
                  <tr>
                    <td colSpan="4">
                      <button className="btn btn-secondary btn-block" onClick={() => {
                        const query = (window.prompt('Новый запрос') || '').trim();
                        if (query) addKeyword(query, null, keywords.length);
                      }}>+ Добавить запрос</button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* 2. Уникализированные запросы */}
      <div className={`seo-uniqualized-panel ${isOverLimit ? 'over-limit' : ''}`}>
        <div className="seo-uniqualized-header" onClick={() => setUniquializedExpanded(!uniqualizedExpanded)}>
          <h4>
            {uniqualizedExpanded ? '▼' : '▶'} Уникализированные запросы
            {!uniqualizedExpanded && (
              <span className={isOverLimit ? 'text-danger' : ''}> ({uniqualizedCount} шт., {uniqualizedChars} симв.)</span>
            )}
          </h4>
          {uniqualizedExpanded && (
            <div style={{ display: 'flex', gap: '8px' }} onClick={e => e.stopPropagation()}>
              <button className="btn btn-sm btn-secondary" onClick={copyUniqualized}>Копировать</button>
              <button className="btn btn-sm btn-secondary" onClick={copyUniquializedAsColumn}>Копировать в столбец</button>
              <button className="btn btn-sm btn-secondary" onClick={copyUniquializedWithPopularity}>Копировать в столбец с частотностью</button>
              <button
                className="btn btn-sm"
                style={{ background: 'var(--action-bg)', color: 'var(--action-fg)', fontWeight: 700, borderRadius: '999px', padding: '4px 14px' }}
                onClick={openProductPickerModal}
                disabled={!computedData.uniqualized_queries || addToProductSeoBusy}
                title="Выбрать товар и записать запросы в поле «Ключевые слова» на OZON"
              >
                {addToProductSeoBusy ? 'Отправка...' : '+ Добавить в SEO товару'}
              </button>
              <button
                className="btn btn-sm"
                style={{ background: '#22c55e', color: '#fff', fontWeight: 600, borderRadius: '999px', padding: '4px 14px' }}
                onClick={openAddToAutoSeoDictModal}
                disabled={!computedData.uniqualized_queries || addToAutoSeoBusy}
                title="Добавить запросы в словарь семантики АвтоSEO"
              >
                {addToAutoSeoBusy ? 'Добавление...' : '+ Добавить в словарь в АвтоSEO'}
              </button>
            </div>
          )}
          {uniqualizedExpanded && (addToProductSeoMessage || addToAutoSeoMessage) && (
            <div style={{ padding: '4px 16px 8px', display: 'flex', flexDirection: 'column', gap: 4 }}>
              {addToProductSeoMessage && (
                <span style={{ fontSize: '0.85rem', fontWeight: 600, color: addToProductSeoMessage.type === 'error' ? '#ef4444' : '#22c55e' }}>
                  {addToProductSeoMessage.text}
                </span>
              )}
              {addToAutoSeoMessage && (
                <span style={{ fontSize: '0.85rem', fontWeight: 600, color: addToAutoSeoMessage.type === 'error' ? '#ef4444' : '#22c55e' }}>
                  {addToAutoSeoMessage.text}
                </span>
              )}
            </div>
          )}
        </div>
        {uniqualizedExpanded && (
          <>
            <div style={{ padding: '6px 16px', background: 'rgba(34,197,94,0.07)', borderBottom: '1px solid var(--border)', fontSize: '0.82rem', color: 'var(--ink-soft)', display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center' }}>
              {hasPlusWords && (
                <span>
                  После плюс-фильтра: <strong>{computedData.plus_filtered_queries.length}</strong> запросов
                  {' → '} после минус-фильтра: <strong>{computedData.filtered_queries.length}</strong> запросов
                </span>
              )}
              <span>Прямой охват: <strong style={{ color: 'var(--accent, #6366f1)' }}>{directReach.toLocaleString('ru-RU')}</strong></span>
              <span>Расширенный охват: <strong style={{ color: 'var(--accent, #6366f1)' }}>{extendedReach == null ? 'н/д на больших объёмах' : extendedReach.toLocaleString('ru-RU')}</strong></span>
            </div>
            <div
              className="seo-uniqualized-box"
              onContextMenu={e => {
                const sel = window.getSelection?.()?.toString?.().trim();
                if (!sel) return;
                e.preventDefault();
                setCtxMenu({ x: e.clientX, y: e.clientY, text: sel });
              }}
            >
              {computedData.uniqualized_queries || '(пусто)'}
            </div>
            {/* Product Selector */}
            <div className="seo-product-selector" style={{ borderTop: '1px solid var(--border)', marginTop: 0 }}>
              <h4 style={{ margin: '0 0 12px 0' }}>Выбор товаров</h4>
              <p style={{ margin: '-8px 0 12px 0', fontSize: '0.82rem', color: 'var(--ink-soft)' }}>
                Общий выбор для кнопок «Перенести в SEO» и «+ Добавить в SEO товару» выше — укажите, к каким товарам применить уникализированные запросы.
              </p>
              <div className="selector-row">
                <div className="product-search-wrapper">
                  <input type="text" placeholder="Поиск товара по артикулу, SKU, названию..." value={productSearch}
                    onChange={e => setProductSearch(e.target.value)} className="selector-input" />
                  {productResults.length > 0 && (
                    <div className="product-search-results" onClick={e => e.stopPropagation()}>
                      {productResults.map((product, idx) => {
                        const isSelected = !!selectedProducts.find(p => p.offer_id === product[2]);
                        return (
                          <div key={idx} className={`product-search-result-item${isSelected ? ' selected' : ''}`} onClick={e => addProduct(product, e.ctrlKey || e.metaKey)}>
                            <span style={{ float: 'right', color: isSelected ? '#22c55e' : 'transparent' }}>✓</span>
                            <strong>{product[1]}</strong><br />
                            <small>Артикул: {product[2]}, SKU: {product[3]}</small>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                {selectedProducts.length > 0 && (
                  <button className="btn btn-sm btn-ghost" onClick={() => setSelectedProducts([])}>Очистить выбор</button>
                )}
              </div>
              {selectedProducts.length > 0 && (
                <div className="selected-products-list">
                  <div className="selected-products-header">Выбрано товаров: {selectedProducts.length}</div>
                  <div className="selected-products-items">
                    {selectedProducts.map(product => (
                      <div key={product.offer_id} className="selected-product-chip">
                        {product.image && <img src={product.image} alt="" className="selected-product-thumb" onError={e => e.target.style.display = 'none'} />}
                        <div className="selected-product-info">
                          <span className="selected-product-name" title={product.name}>
                            {product.name?.length > 40 ? product.name.slice(0, 40) + '...' : product.name}
                          </span>
                          <span className="selected-product-id">Арт: {product.offer_id}</span>
                        </div>
                        <button className="selected-product-remove" onClick={() => setSelectedProducts(prev => prev.filter(p => p.offer_id !== product.offer_id))}>×</button>
                      </div>
                    ))}
                  </div>
                  <div className="transfer-action">
                    <button className="btn btn-primary btn-transfer" onClick={transferToSeo}
                      disabled={!computedData.uniqualized_queries || busy}>
                      Перенести в SEO ({selectedProducts.length} товар{selectedProducts.length === 1 ? '' : 'ов'})
                    </button>
                    <span className="transfer-hint">Уникализированные запросы будут перенесены в Аннотацию</span>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* 3. Уникальные слова */}
      <div className="seo-words-zone">
        <div className="seo-words-header" onClick={() => setUniqueWordsExpanded(!uniqueWordsExpanded)}>
          <h4>{uniqueWordsExpanded ? '▼' : '▶'} Уникальные слова ({computedData.unique_words.length} шт.)</h4>
        </div>
        {uniqueWordsExpanded && (
          <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="text" placeholder="Поиск по уникальным словам..." value={uniqueWordsSearchQuery}
                onChange={e => { setUniqueWordsSearchQuery(e.target.value); setUniqueWordsStep(0); }}
                className="table-search" style={{ flex: 1 }} />
              <label className="page-size-label">Показать:
                <select value={uniqueWordsPageSize === Infinity ? 'all' : uniqueWordsPageSize}
                  onChange={e => { setUniqueWordsPageSize(e.target.value === 'all' ? Infinity : parseInt(e.target.value)); setUniqueWordsStep(0); }}
                  className="page-size-select">
                  <option value="50">50</option><option value="250">250</option>
                  <option value="500">500</option><option value="1000">1000</option><option value="all">Все</option>
                </select>
              </label>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="text" placeholder="Фильтр для минусования..." value={uniqueWordsFilterInput}
                  onChange={e => { setUniqueWordsFilterInput(e.target.value); setUniqueWordsStep(0); }}
                  className="cell-input" style={{ flex: 1 }} />
                <button className="btn btn-secondary btn-sm" onClick={bulkMinusFiltered}
                  disabled={busy || !uniqueWordsFilterInput.trim() || filterInputWords.length === 0}
                  style={{ borderRadius: '999px' }}>
                  Отминусовать ({filterInputWords.length})
                </button>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button className="btn btn-secondary btn-sm" style={{ borderRadius: '999px' }} disabled={busy}
                  onClick={() => bulkMinus(computedData.unique_words.filter(w => /[a-zA-Z]/.test(w)), 'с латиницей')}>
                  Минусовать латиницу
                </button>
                <button className="btn btn-secondary btn-sm" style={{ borderRadius: '999px' }} disabled={busy}
                  onClick={() => bulkMinus(computedData.unique_words.filter(w => /[^\p{L}\p{N}\s]/u.test(w)), 'с символами')}>
                  Минусовать символы
                </button>
                <button className="btn btn-secondary btn-sm" style={{ borderRadius: '999px' }} disabled={busy}
                  onClick={() => bulkMinus(computedData.unique_words.filter(w => /\d/.test(w)), 'с цифрами')}>
                  Минусовать цифры
                </button>
              </div>
            </div>
            <div className="unique-words-panel">
              <div className="unique-words-list">
                {uniqueWordsPage.map((word, widx) => (
                  <div key={widx} className="unique-word-item">
                    {word}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flexShrink: 0 }}>
                      <button className="btn-icon btn-remove" onClick={() => addMinusWord(word)} title="В минус-слова">−</button>
                      <button className="btn-icon btn-add" onClick={() => addPlusWord(word)} title="В плюс-слова" style={{ color: '#22c55e' }}>+</button>
                    </div>
                  </div>
                ))}
              </div>
              {uniqueWordsPageSize !== Infinity && uniqueWordsTotalPages > 1 && (
                <div className="unique-words-pager">
                  <button className="btn btn-sm" onClick={() => setUniqueWordsStep(Math.max(0, uniqueWordsStep - 1))} disabled={uniqueWordsStep === 0}>←</button>
                  <span>{uniqueWordsPageStart + 1}-{uniqueWordsPageEnd} из {uniqueWordsTotal}</span>
                  <button className="btn btn-sm" onClick={() => setUniqueWordsStep(Math.min(uniqueWordsTotalPages - 1, uniqueWordsStep + 1))} disabled={uniqueWordsStep >= uniqueWordsTotalPages - 1}>→</button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* 4. Плюс-слова | Минус-слова */}
      <div className="seo-words-zone">
        <div className="seo-words-header" onClick={() => setPlusMinusExpanded(!plusMinusExpanded)}>
          <h4>
            {plusMinusExpanded ? '▼' : '▶'} Плюс-слова и Минус-слова
            {!plusMinusExpanded && (
              <span style={{ fontWeight: 400, fontSize: '0.85rem', marginLeft: 8, color: 'var(--ink-soft)' }}>
                · <span style={{ color: '#22c55e' }}>плюс: {plusWords.length}</span>, минус: {minusWords.length}
              </span>
            )}
          </h4>
        </div>
        {plusMinusExpanded && (
          <div className="seo-words-content">

            {/* Плюс-слова (левая колонка) */}
            <div className="seo-words-column" style={{ width: '50%' }}>
              <div className="seo-words-column-header">
                <h5 style={{ color: '#22c55e' }}>Плюс-слова ({plusWords.length})</h5>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button className="btn btn-secondary btn-sm"
                    onClick={() => { if (window.confirm('Очистить все плюс-слова?')) authFetch(`${API_BASE_URL}/api/v1/ozon-seo/${accountId}/plus-words`, { method: 'DELETE' }).then(loadData).catch(error => alert('Не удалось очистить плюс-слова: ' + (error.message || error))); }}
                    disabled={busy || plusWords.length === 0} style={{ fontSize: '0.75rem' }}>
                    Очистить всё
                  </button>
                  <input type="text" placeholder="Поиск..." value={plusWordsSearchQuery}
                    onChange={e => { setPlusWordsSearchQuery(e.target.value); setPlusWordsStep(0); }}
                    className="table-search" />
                  <label className="page-size-label">
                    <select value={plusWordsPageSize === Infinity ? 'all' : plusWordsPageSize}
                      onChange={e => { setPlusWordsPageSize(e.target.value === 'all' ? Infinity : parseInt(e.target.value)); setPlusWordsStep(0); }}
                      className="page-size-select">
                      <option value="50">50</option><option value="250">250</option><option value="all">Все</option>
                    </select>
                  </label>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', padding: '4px 0 8px', fontSize: '0.82rem', color: 'var(--ink-soft)', borderBottom: '1px solid var(--border)' }}>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  Режим:
                  <select value={plusSettings.match_mode} onChange={e => updatePlusSettings({ match_mode: e.target.value })} className="page-size-select">
                    <option value="strict">Жёсткий</option>
                    <option value="soft">Мягкий</option>
                  </select>
                </label>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  Мин. совпадений:
                  <input type="number" min={1} max={Math.max(plusWords.length, 1)}
                    value={plusSettings.min_count}
                    onChange={e => updatePlusSettings({ min_count: parseInt(e.target.value) || 1 })}
                    className="cell-input" style={{ width: 50 }} />
                  <span>из {plusWords.length}</span>
                </label>
                {hasPlusWords && <span style={{ marginLeft: 'auto', color: '#22c55e' }}>{computedData.plus_filtered_queries.length} прошли</span>}
              </div>
              {pendingPlusWords.length > 0 && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                  <strong style={{ color: '#22c55e' }}>К отправке: {pendingPlusWords.length}</strong>
                  {pendingPlusWords.map(word => (
                    <span key={`pending-plus-${word}`} className="minus-word-item" style={{ borderColor: 'rgba(34,197,94,0.4)' }}>
                      {word}
                      <button className="btn-icon btn-remove" onClick={() => setPendingPlusWords(prev => prev.filter(item => item !== word))} title="Убрать из очереди">×</button>
                    </span>
                  ))}
                  <button type="button" className="btn btn-secondary btn-sm" onClick={applyPendingPlusWords} disabled={busy}>Применить</button>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setPendingPlusWords([])} disabled={busy}>Очистить очередь</button>
                </div>
              )}
              <div className="minus-words-panel">
                <div className="minus-words-list">
                  {plusWordsPageData.map(pw => (
                    <div key={pw.id} className="minus-word-item" style={{ borderColor: 'rgba(34,197,94,0.4)' }}>
                      {pw.word}
                      <button className="btn-icon btn-remove" onClick={() => deletePlusWord(pw.id)} title="Удалить">−</button>
                    </div>
                  ))}
                </div>
                {plusWordsPageSize !== Infinity && plusWordsTotalPages > 1 && (
                  <div className="unique-words-pager">
                    <button className="btn btn-sm" onClick={() => setPlusWordsStep(Math.max(0, plusWordsStep - 1))} disabled={plusWordsStep === 0}>←</button>
                    <span>{plusWordsPageStart + 1}-{plusWordsPageEnd} из {plusWordsTotal}</span>
                    <button className="btn btn-sm" onClick={() => setPlusWordsStep(Math.min(plusWordsTotalPages - 1, plusWordsStep + 1))} disabled={plusWordsStep >= plusWordsTotalPages - 1}>→</button>
                  </div>
                )}
                <div className="minus-word-input-row">
                  <input type="text" placeholder="Добавить плюс-слово..." className="cell-input"
                    value={plusWordInput} onChange={e => setPlusWordInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && plusWordInput.trim()) { addPlusWord(plusWordInput.trim()); setPlusWordInput(''); } }}
                    style={{ flex: 1 }} />
                  <button type="button" className="btn btn-ghost"
                    onClick={() => { if (plusWordInput.trim()) { addPlusWord(plusWordInput.trim()); setPlusWordInput(''); } }}
                    style={{ marginLeft: 8 }}>Добавить</button>
                </div>
                <div style={{ marginTop: 4 }}>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowPlusWordsPasteModal(true)}
                    style={{ fontSize: '0.78rem' }}>+ Вставить список</button>
                </div>
                <div className="minus-word-input-row" style={{ marginTop: 8 }}>
                  <input type="text" placeholder="Удалить по точному совпадению..." className="cell-input"
                    value={plusWordDeleteExact} onChange={e => setPlusWordDeleteExact(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && plusWordDeleteExact.trim()) deletePlusWordByExact(); }}
                    style={{ flex: 1 }} />
                  <button type="button" className="btn btn-secondary" onClick={deletePlusWordByExact}
                    disabled={busy || !plusWordDeleteExact.trim()} style={{ marginLeft: 8 }}>Удалить</button>
                </div>
              </div>
            </div>

            {/* Минус-слова (правая колонка) */}
            <div className="seo-words-column" style={{ width: '50%' }}>
              <div className="seo-words-column-header">
                <h5>Минус-слова ({minusWords.length})</h5>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button className="btn btn-secondary btn-sm"
                    onClick={() => { if (window.confirm('Очистить все минус-слова?')) authFetch(`${API_BASE_URL}/api/v1/ozon-seo/${accountId}/minus-words`, { method: 'DELETE' }).then(loadData).catch(error => alert('Не удалось очистить минус-слова: ' + (error.message || error))); }}
                    disabled={busy || minusWords.length === 0} style={{ fontSize: '0.75rem' }}>
                    Очистить всё
                  </button>
                  <input type="text" placeholder="Поиск..." value={minusWordsSearchQuery}
                    onChange={e => { setMinusWordsSearchQuery(e.target.value); setMinusWordsStep(0); }}
                    className="table-search" />
                  <label className="page-size-label">
                    <select value={minusWordsPageSize === Infinity ? 'all' : minusWordsPageSize}
                      onChange={e => { setMinusWordsPageSize(e.target.value === 'all' ? Infinity : parseInt(e.target.value)); setMinusWordsStep(0); }}
                      className="page-size-select">
                      <option value="50">50</option><option value="250">250</option>
                      <option value="500">500</option><option value="1000">1000</option><option value="all">Все</option>
                    </select>
                  </label>
                </div>
              </div>
              {pendingMinusWords.length > 0 && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                  <strong>К отправке: {pendingMinusWords.length}</strong>
                  {pendingMinusWords.map(word => (
                    <span key={`pending-minus-${word}`} className="minus-word-item">
                      {word}
                      <button className="btn-icon btn-remove" onClick={() => setPendingMinusWords(prev => prev.filter(item => item !== word))} title="Убрать из очереди">×</button>
                    </span>
                  ))}
                  <button type="button" className="btn btn-secondary btn-sm" onClick={applyPendingMinusWords} disabled={busy}>Применить</button>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setPendingMinusWords([])} disabled={busy}>Очистить очередь</button>
                </div>
              )}
              <div className="minus-words-panel" style={{ marginTop: 8 }}>
                <div className="minus-words-list">
                  {minusWordsPage.map(mw => (
                    <div key={mw.id} className="minus-word-item">
                      {mw.word}
                      <button className="btn-icon btn-remove" onClick={() => deleteMinusWord(mw.id)} title="Удалить">−</button>
                    </div>
                  ))}
                </div>
                {minusWordsPageSize !== Infinity && minusWordsTotalPages > 1 && (
                  <div className="unique-words-pager">
                    <button className="btn btn-sm" onClick={() => setMinusWordsStep(Math.max(0, minusWordsStep - 1))} disabled={minusWordsStep === 0}>←</button>
                    <span>{minusWordsPageStart + 1}-{minusWordsPageEnd} из {minusWordsTotal}</span>
                    <button className="btn btn-sm" onClick={() => setMinusWordsStep(Math.min(minusWordsTotalPages - 1, minusWordsStep + 1))} disabled={minusWordsStep >= minusWordsTotalPages - 1}>→</button>
                  </div>
                )}
                <div className="minus-word-input-row">
                  <input type="text" placeholder="Добавить минус-слово..." className="cell-input"
                    value={minusWordInput} onChange={e => setMinusWordInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && minusWordInput.trim()) { addMinusWord(minusWordInput.trim()); setMinusWordInput(''); } }}
                    style={{ flex: 1 }} />
                  <button type="button" className="btn btn-ghost"
                    onClick={() => { if (minusWordInput.trim()) { addMinusWord(minusWordInput.trim()); setMinusWordInput(''); } }}
                    style={{ marginLeft: 8 }}>Добавить</button>
                </div>
                <div style={{ marginTop: 4 }}>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowMinusWordsPasteModal(true)}
                    style={{ fontSize: '0.78rem' }}>+ Вставить список</button>
                </div>
                <div className="minus-word-input-row" style={{ marginTop: 8 }}>
                  <input type="text" placeholder="Удалить по точному совпадению..." className="cell-input"
                    value={minusWordDeleteExact} onChange={e => setMinusWordDeleteExact(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && minusWordDeleteExact.trim()) deleteMinusWordByExact(); }}
                    style={{ flex: 1 }} />
                  <button type="button" className="btn btn-secondary" onClick={deleteMinusWordByExact}
                    disabled={busy || !minusWordDeleteExact.trim()} style={{ marginLeft: 8 }}>Удалить</button>
                </div>
              </div>
            </div>

          </div>
        )}
      </div>

      {/* Modals */}

      <Modal open={showPasteModal} onClose={() => setShowPasteModal(false)} title="Вставить данные">
        <p style={{ marginBottom: 12, color: 'var(--ink-soft)' }}>Скопируйте два столбца из таблицы и вставьте</p>
        <textarea value={csvText} onChange={e => setCsvText(e.target.value)}
          onKeyDown={e => { if (e.ctrlKey && e.key === 'Enter') handleCSVImport(); }}
          placeholder={"Запрос1\t1000\nЗапрос2\t500"} rows="10"
          style={{ width: '100%', fontFamily: 'monospace', fontSize: '0.9rem', boxSizing: 'border-box' }} />
        <p style={{ margin: '8px 0 16px', fontSize: '0.85rem', color: 'var(--ink-soft)' }}>Ctrl+Enter для импорта</p>
        <div className="modal__actions">
          <button className="btn btn-secondary" onClick={() => setShowPasteModal(false)}>Отмена</button>
          <button className="btn btn-primary" onClick={handleCSVImport} disabled={busy}>Импортировать</button>
        </div>
      </Modal>

      <Modal open={showMinusWordsPasteModal} onClose={() => setShowMinusWordsPasteModal(false)} title="Вставить минус-слова">
        <p style={{ marginBottom: 12, color: 'var(--ink-soft)' }}>Каждое слово с новой строки</p>
        <textarea value={minusWordsText} onChange={e => setMinusWordsText(e.target.value)}
          onKeyDown={e => { if (e.ctrlKey && e.key === 'Enter') handleMinusWordsImport(); }}
          placeholder={"слово1\nслово2\nслово3"} rows="15"
          style={{ width: '100%', fontFamily: 'monospace', fontSize: '0.9rem', boxSizing: 'border-box' }} />
        <p style={{ margin: '8px 0 16px', fontSize: '0.85rem', color: 'var(--ink-soft)' }}>Ctrl+Enter для импорта</p>
        <div className="modal__actions">
          <button className="btn btn-secondary" onClick={() => setShowMinusWordsPasteModal(false)}>Отмена</button>
          <button className="btn btn-primary" onClick={handleMinusWordsImport} disabled={busy}>Импортировать</button>
        </div>
      </Modal>

      <Modal open={showPlusWordsPasteModal} onClose={() => setShowPlusWordsPasteModal(false)} title="Вставить плюс-слова">
        <p style={{ marginBottom: 12, color: 'var(--ink-soft)' }}>Каждое слово с новой строки</p>
        <textarea value={plusWordsText} onChange={e => setPlusWordsText(e.target.value)}
          onKeyDown={e => { if (e.ctrlKey && e.key === 'Enter') handlePlusWordsImport(); }}
          placeholder={"слово1\nслово2\nслово3"} rows="15"
          style={{ width: '100%', fontFamily: 'monospace', fontSize: '0.9rem', boxSizing: 'border-box' }} />
        <p style={{ margin: '8px 0 16px', fontSize: '0.85rem', color: 'var(--ink-soft)' }}>Ctrl+Enter для импорта</p>
        <div className="modal__actions">
          <button className="btn btn-secondary" onClick={() => setShowPlusWordsPasteModal(false)}>Отмена</button>
          <button className="btn btn-primary" onClick={handlePlusWordsImport} disabled={busy}>Импортировать</button>
        </div>
      </Modal>

      <Modal open={showAutoSeoDictModal} onClose={() => setShowAutoSeoDictModal(false)} title="Добавить в словарь АвтоSEO">
        <p style={{ marginBottom: 12, color: 'var(--ink-soft)' }}>
          Будет добавлено <strong>{uniqualizedCount}</strong> запрос(ов) в выбранный словарь семантики АвтоSEO.
        </p>
        <label style={{ display: 'grid', gap: 6, fontSize: '0.9rem', color: 'var(--ink-soft)', marginBottom: 12 }}>
          Словарь:
          {autoSeoDictionaries.length > 0 ? (
            <select value={autoSeoDictionaryId || ''} onChange={e => setAutoSeoDictionaryId(e.target.value || null)}
              className="cell-input" style={{ width: '100%', boxSizing: 'border-box' }}>
              {autoSeoDictionaries.map(d => (
                <option key={d.id} value={d.id}>{d.name} ({(d.keyword_count || 0).toLocaleString('ru-RU')})</option>
              ))}
            </select>
          ) : (
            <span>Словарей ещё нет — создайте первый ниже.</span>
          )}
        </label>
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <input
            type="text"
            placeholder="Название нового словаря"
            value={autoSeoNewDictName}
            onChange={e => setAutoSeoNewDictName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') createAutoSeoDictionary(); }}
            className="cell-input"
            style={{ flex: 1, boxSizing: 'border-box' }}
          />
          <button type="button" className="btn btn-secondary" onClick={createAutoSeoDictionary}
            disabled={autoSeoCreatingDict || !autoSeoNewDictName.trim()}>
            {autoSeoCreatingDict ? 'Создание...' : '+ Создать'}
          </button>
        </div>
        <div className="modal__actions">
          <button className="btn btn-secondary" onClick={() => setShowAutoSeoDictModal(false)}>Отмена</button>
          <button className="btn btn-primary" onClick={confirmAddUniqualizedToAutoSeoDictionary} disabled={addToAutoSeoBusy}>
            {addToAutoSeoBusy ? 'Добавление...' : 'Добавить'}
          </button>
        </div>
      </Modal>

      <Modal
        open={showProductPickerModal}
        onClose={() => { setShowProductPickerModal(false); setPickerResults([]); }}
        title="Выбрать товар для SEO"
        wide
      >
        <p style={{ margin: '0 0 12px', fontSize: '0.85rem', color: 'var(--ink-soft)' }}>
          Найдите и выберите товар(ы) — уникализированные запросы будут записаны в поле «Ключевые слова» на OZON именно для них.
        </p>
        <div className="product-search-wrapper">
          <input
            type="text"
            placeholder="Поиск товара по артикулу, SKU, названию..."
            value={pickerSearch}
            onChange={e => setPickerSearch(e.target.value)}
            className="selector-input"
            style={{ width: '100%', boxSizing: 'border-box' }}
            autoFocus
          />
          {pickerResults.length > 0 && (
            <div className="product-search-results">
              {pickerResults.map((product, idx) => {
                const isSelected = !!pickerSelected.find(p => p.offer_id === product[2]);
                return (
                  <div key={idx} className={`product-search-result-item${isSelected ? ' selected' : ''}`} onClick={() => togglePickerProduct(product)}>
                    <span style={{ float: 'right', color: isSelected ? '#22c55e' : 'transparent' }}>✓</span>
                    <strong>{product[1]}</strong><br />
                    <small>Артикул: {product[2]}, SKU: {product[3]}</small>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {pickerSelected.length > 0 && (
          <div className="selected-products-list" style={{ marginTop: 12 }}>
            <div className="selected-products-header">Выбрано товаров: {pickerSelected.length}</div>
            <div className="selected-products-items">
              {pickerSelected.map(product => (
                <div key={product.offer_id} className="selected-product-chip">
                  {product.image && <img src={product.image} alt="" className="selected-product-thumb" onError={e => e.target.style.display = 'none'} />}
                  <div className="selected-product-info">
                    <span className="selected-product-name" title={product.name}>
                      {product.name?.length > 40 ? product.name.slice(0, 40) + '...' : product.name}
                    </span>
                    <span className="selected-product-id">Арт: {product.offer_id}</span>
                  </div>
                  <button className="selected-product-remove" onClick={() => setPickerSelected(prev => prev.filter(p => p.offer_id !== product.offer_id))}>×</button>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="modal__actions" style={{ marginTop: 16 }}>
          <button className="btn btn-secondary" onClick={() => { setShowProductPickerModal(false); setPickerResults([]); }}>Отмена</button>
          <button className="btn btn-primary" onClick={confirmAddUniqualizedToProductSeo} disabled={addToProductSeoBusy || pickerSelected.length === 0}>
            {addToProductSeoBusy ? 'Отправка...' : `Добавить в SEO (${pickerSelected.length})`}
          </button>
        </div>
      </Modal>

      {ctxMenu && (
        <div
          style={{ position: 'fixed', top: ctxMenu.y, left: ctxMenu.x, zIndex: 9999, background: 'var(--glass-98, #fff)', border: '1px solid rgba(15,23,42,0.12)', borderRadius: 8, boxShadow: '0 4px 16px rgba(15,23,42,0.15)', overflow: 'hidden', minWidth: 180 }}
          onMouseLeave={() => setCtxMenu(null)}
        >
          <button
            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '9px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.88rem', borderBottom: '1px solid rgba(15,23,42,0.07)' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(34,197,94,0.1)'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}
            onClick={() => { addPlusWord(ctxMenu.text); setCtxMenu(null); }}
          >
            + Добавить в плюсы
          </button>
          <button
            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '9px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.88rem' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.1)'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}
            onClick={() => { addMinusWord(ctxMenu.text); setCtxMenu(null); }}
          >
            − Добавить в минусы
          </button>
        </div>
      )}

    </div>
  );
}

export default OzonSeoUniqualization;
